import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDownIcon, GlobeAltIcon } from '@heroicons/react/24/outline';
import { cn } from '../utils';

// SVG Flag Components
const USFlag = ({ className = "w-4 h-3" }) => (
  <svg viewBox="0 0 640 480" className={className}>
    <defs>
      <clipPath id="us-a">
        <path fillOpacity=".7" d="M0 0h640v480H0z"/>
      </clipPath>
    </defs>
    <g fillRule="evenodd" clipPath="url(#us-a)">
      <g strokeWidth="1pt">
        <path fill="#bd3d44" d="M0 0h640v37H0zm0 74h640v37H0zm0 74h640v37H0zm0 74h640v37H0zm0 74h640v37H0zm0 74h640v37H0zm0 74h640v37H0z"/>
        <path fill="#fff" d="M0 37h640v37H0zm0 74h640v37H0zm0 74h640v37H0zm0 74h640v37H0zm0 74h640v37H0zm0 74h640v37H0z"/>
      </g>
      <path fill="#192f5d" d="M0 0h364v259H0z"/>
      <g fill="#fff">
        <g className="flag-stars">
          <path d="M30.4 11l2.3 7.1h7.5l-6.1 4.4 2.3 7.1-6.1-4.4-6.1 4.4 2.3-7.1-6.1-4.4h7.5zm60.8 0l2.3 7.1h7.5l-6.1 4.4 2.3 7.1-6.1-4.4-6.1 4.4 2.3-7.1-6.1-4.4h7.5zm60.8 0l2.3 7.1h7.5l-6.1 4.4 2.3 7.1-6.1-4.4-6.1 4.4 2.3-7.1-6.1-4.4h7.5zm60.8 0l2.3 7.1h7.5l-6.1 4.4 2.3 7.1-6.1-4.4-6.1 4.4 2.3-7.1-6.1-4.4h7.5zm60.8 0l2.3 7.1h7.5l-6.1 4.4 2.3 7.1-6.1-4.4-6.1 4.4 2.3-7.1-6.1-4.4h7.5zm60.8 0l2.3 7.1h7.5l-6.1 4.4 2.3 7.1-6.1-4.4-6.1 4.4 2.3-7.1-6.1-4.4h7.5z"/>
        </g>
      </g>
    </g>
  </svg>
);

const GermanFlag = ({ className = "w-4 h-3" }) => (
  <svg viewBox="0 0 640 480" className={className}>
    <path fill="#ffce00" d="M0 320h640v160H0z"/>
    <path d="M0 0h640v160H0z"/>
    <path fill="#d00" d="M0 160h640v160H0z"/>
  </svg>
);

const languages = [
  {
    code: 'en',
    name: 'English',
    flag: USFlag,
  },
  {
    code: 'de', 
    name: 'Deutsch',
    flag: GermanFlag,
  },
];

const LanguageSelector = ({ variant = 'dropdown' }) => {
  const { i18n, t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const changeLanguage = (langCode) => {
    i18n.changeLanguage(langCode);
    setIsOpen(false);
    
    // Save to localStorage for persistence
    localStorage.setItem('i18nextLng', langCode);
    
    // Update HTML lang attribute
    document.documentElement.lang = langCode;
  };

  if (variant === 'compact') {
    // Compact version for header - just flags
    return (
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center space-x-1 p-2 text-dark-300 hover:text-white transition-colors"
          title={t('language.selector.changeLanguage')}
        >
          <currentLanguage.flag className="w-5 h-4" />
          <ChevronDownIcon className="w-3 h-3" />
        </button>

        {isOpen && (
          <div className="absolute right-0 mt-2 w-32 bg-dark-700 border border-dark-600 rounded-lg shadow-lg z-50">
            {languages.map((language) => (
              <button
                key={language.code}
                onClick={() => changeLanguage(language.code)}
                className={cn(
                  'w-full flex items-center space-x-2 px-3 py-2 text-sm hover:bg-dark-600 transition-colors',
                  currentLanguage.code === language.code ? 'text-primary-400 bg-dark-600' : 'text-dark-200',
                  'first:rounded-t-lg last:rounded-b-lg'
                )}
              >
                <language.flag className="w-4 h-3" />
                <span>{language.name}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Full dropdown version for settings page
  return (
    <div className="relative" ref={dropdownRef}>
      <label className="block text-sm font-medium text-dark-200 mb-2">
        <GlobeAltIcon className="h-4 w-4 inline mr-2" />
        {t('language.selector.changeLanguage')}
      </label>
      
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
      >
        <div className="flex items-center space-x-2">
          <currentLanguage.flag className="w-5 h-4" />
          <span>{currentLanguage.name}</span>
        </div>
        <ChevronDownIcon className="w-4 h-4 text-dark-400" />
      </button>

      {isOpen && (
        <div className="absolute left-0 right-0 mt-1 bg-dark-700 border border-dark-600 rounded-lg shadow-lg z-50">
          {languages.map((language) => (
            <button
              key={language.code}
              onClick={() => changeLanguage(language.code)}
              className={cn(
                'w-full flex items-center space-x-3 px-3 py-3 text-sm hover:bg-dark-600 transition-colors',
                currentLanguage.code === language.code ? 'text-primary-400 bg-dark-600' : 'text-dark-200',
                'first:rounded-t-lg last:rounded-b-lg border-b border-dark-600 last:border-b-0'
              )}
            >
              <language.flag className="w-5 h-4" />
              <span>{language.name}</span>
              {currentLanguage.code === language.code && (
                <span className="ml-auto text-primary-400">✓</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSelector;